import {
    ContactDetails,
    InsuranceCareTeamMetadata,
    ProviderCareTeamMetadata,
    SupportCareTeamMetadata,
    User,
} from './DynamoTypes';
import {
    PrimaryRecordAllergy,
    PrimaryRecordCondition,
    PrimaryRecordMedication,
    PrimaryRecordResource,
    PrimaryRecordVisit,
    ResourceMetadata,
} from './MedicalDataTypes';

export type ReportProviderCareTeamMember = ProviderCareTeamMetadata & ContactDetails;
export type ReportSupportCareTeamMember = SupportCareTeamMetadata & ContactDetails;
export type ReportInsuranceCareTeamMember = InsuranceCareTeamMetadata & ContactDetails;

export type ContactDetailsDerivedData = {
    age: number | null;
};

export type DerivedValues<TObject extends object, TDerivedData extends object> = TObject & {
    derived: TDerivedData;
};

export type ReportResource<TResource extends PrimaryRecordResource> = {
    resource: TResource;
    metadata: ResourceMetadata;
    createdByUser?: string;
};

export type ReportMedicalDataInputData = {
    medications: ReportResource<PrimaryRecordMedication>[];
    conditions: ReportResource<PrimaryRecordCondition>[];
    allergies: ReportResource<PrimaryRecordAllergy>[];
    visits: ReportResource<PrimaryRecordVisit>[];
};

export type ReportProfileInfoInputData = {
    subject: DerivedValues<ContactDetails, ContactDetailsDerivedData> & {
        codeStatus: string | null;
    };
    emergencyContacts: DerivedValues<ContactDetails, ContactDetailsDerivedData>[];
    noKnownDrugAllergies?: boolean;
};

export type ReportCareTeamMembersInputData = {
    providerCareTeamMembers: ReportProviderCareTeamMember[];
    supportCareTeamMembers: ReportSupportCareTeamMember[];
    insuranceCareTeamMembers: ReportInsuranceCareTeamMember[];
};

export type ReportObjectWithoutDerivedValues<T> =
    T extends Array<infer U>
        ? Array<ReportObjectWithoutDerivedValues<U>>
        : T extends object
          ? { [P in keyof T as Exclude<P, 'derived'>]: ReportObjectWithoutDerivedValues<T[P]> }
          : T;

export type ReportUserInfo = {
    user: User;
};
export type Professional = {
    orgName: string;
    phoneNumber: string;
    email: string;
    logo?: string;
};
export type ReportProfessionalInfo = {
    professional: Professional[];
};
export type ReportGenerationInfo = {
    /**
     * ISO 8601 UTC timestamp of the moment this report's data was assembled and sent to be rendered.
     *
     * This is the only field that describes the report itself. Every other date in the payload
     * describes the record being reported on - notably `subject.createdAt`, which is when the
     * medical profile was created, not when the report was run.
     */
    reportGeneratedAt: string;
};

export type ReportInputData = ReportMedicalDataInputData &
    ReportProfileInfoInputData &
    ReportUserInfo &
    ReportProfessionalInfo &
    ReportGenerationInfo;

export type ReportType = 'active-medications' | 'all-medications' | 'summary-report';
